// src/pages/user-profile-settings/components/ProfileInformation.jsx
import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { useToast } from '../../../context/ToastContext';
import { useLanguage } from '../../../context/LanguageContext';

const ProfileInformation = ({
  user,
  onDataChange,
  onProfileSave,
  onAvatarUpload,
  onAvatarRemove,
  isSaving = false,
  isAvatarUploading = false,
}) => {
  const { success, error } = useToast();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    bio: '',
    location: '',
    website: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const fileInputRef = useRef(null);

  const texts = {
    title: t('Profile Information', 'Profil Bilgileri'),
    subtitle: t('Update your personal information and profile photo', 'Kişisel bilgilerinizi ve profil fotoğrafınızı güncelleyin'),
    fallbackUser: t('User', 'Kullanıcı'),
    avatarHint: t('JPG/PNG, max 5MB', 'JPG/PNG, en fazla 5MB'),
    uploadPhoto: t('Upload Photo', 'Foto Yükle'),
    removePhoto: t('Remove', 'Kaldır'),
    uploading: t('Uploading...', 'Yükleniyor...'),
    fullName: t('Full Name', 'Ad Soyad'),
    fullNamePlaceholder: t('Enter your full name', 'Ad soyad girin'),
    email: t('Email Address', 'E-posta Adresi'),
    emailPlaceholder: t('your.email@example.com', 'ornek@eposta.com'),
    phone: t('Phone Number', 'Telefon Numarası'),
    phonePlaceholder: t('+1 (555) 123-4567', '+90 555 123 45 67'),
    location: t('Location', 'Konum'),
    locationPlaceholder: t('City, State', 'Şehir, İlçe'),
    website: t('Website', 'Web Sitesi'),
    websitePlaceholder: t('https://www.yourwebsite.com', 'https://www.siteniz.com'),
    bio: t('Bio', 'Hakkında'),
    bioPlaceholder: t('Tell us about yourself...', 'Kendinizden kısaca bahsedin...'),
    characterCount: t('characters', 'karakter'),
    saveChanges: t('Save Changes', 'Değişiklikleri Kaydet'),
    saving: t('Saving...', 'Kaydediliyor...'),
    onlyImageFiles: t('Only image files are allowed.', 'Sadece görsel dosyaları yüklenebilir.'),
    imageSizeLimit: t('Image size must be 5MB or less.', 'Görsel boyutu en fazla 5MB olabilir.'),
    photoUpdated: t('Profile photo updated.', 'Profil fotoğrafı güncellendi.'),
    photoUpdateFailed: t('Failed to upload profile photo.', 'Profil fotoğrafı yüklenemedi.'),
    photoRemoved: t('Profile photo removed.', 'Profil fotoğrafı kaldırıldı.'),
    photoRemoveFailed: t('Failed to remove profile photo.', 'Profil fotoğrafı kaldırılamadı.'),
    profileSaved: t('Profile information saved successfully.', 'Profil bilgileri başarıyla kaydedildi.'),
    profileSaveFailed: t('Failed to save profile information.', 'Profil bilgileri kaydedilemedi.'),
    fullNameRequired: t('Full name is required.', 'Ad soyad zorunludur.'),
    emailRequired: t('Email address is required.', 'E-posta adresi zorunludur.'),
    emailInvalid: t('Please enter a valid email address.', 'Lütfen geçerli bir e-posta adresi girin.'),
  };

  useEffect(() => {
    setFormData({
      fullName: user?.fullName || user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      bio: user?.bio || '',
      location: user?.location || '',
      website: user?.website || ''
    });
    setFieldErrors({});
  }, [user]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev?.[field]) {
        return prev;
      }

      const next = { ...prev };
      delete next[field];
      return next;
    });
    onDataChange?.();
  };

  const validateForm = () => {
    const nextErrors = {};
    const trimmedFullName = formData.fullName.trim();
    const trimmedEmail = formData.email.trim();

    if (!trimmedFullName) {
      nextErrors.fullName = texts.fullNameRequired;
    }

    if (!trimmedEmail) {
      nextErrors.email = texts.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      nextErrors.email = texts.emailInvalid;
    }

    return nextErrors;
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      error(texts.onlyImageFiles);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      error(texts.imageSizeLimit);
      return;
    }

    const result = await onAvatarUpload?.(file);
    if (result?.success) {
      success(texts.photoUpdated);
      return;
    }

    error(result?.message || texts.photoUpdateFailed);
  };

  const handleRemoveAvatar = async () => {
    const result = await onAvatarRemove?.();
    if (result?.success) {
      success(texts.photoRemoved);
      return;
    }

    error(result?.message || texts.photoRemoveFailed);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      error(Object.values(validationErrors)[0]);
      return;
    }

    const result = await onProfileSave?.({
      fullName: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      location: formData.location,
      website: formData.website,
      bio: formData.bio,
    });

    if (result?.fieldErrors) {
      setFieldErrors(result.fieldErrors);
    }

    if (result?.success) {
      success(texts.profileSaved);
      return;
    }

    error(result?.message || texts.profileSaveFailed);
  };

  return (
    <div className="bg-surface rounded-lg shadow-elevation-1">
      <div className="px-6 py-4 border-b border-border">
        <h2 className="text-xl font-semibold text-text-primary font-heading">
          {texts.title}
        </h2>
        <p className="text-sm text-text-secondary mt-1">
          {texts.subtitle}
        </p>
      </div>

      <form className="p-6" onSubmit={handleSubmit}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 mb-8">
          <div className="relative group">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-secondary-100 cursor-pointer" onClick={handleAvatarClick}>
              <img
                src={user?.avatar || '/assets/images/profile_default.png'}
                alt="Profile"
                className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-110"
                onError={(e) => {
                  e.target.src = '/assets/images/profile_default.png';
                }}
              />
            </div>
            <button
              type="button"
              onClick={handleAvatarClick}
              className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            >
              <Icon name="Camera" size={20} className="text-white" />
            </button>
          </div>

          <div className="mt-4 sm:mt-0 flex-1">
            <h3 className="text-lg font-medium text-text-primary">{formData.fullName || texts.fallbackUser}</h3>
            <p className="text-text-secondary text-sm mb-3">
              {texts.avatarHint}
            </p>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                type="button"
                onClick={handleAvatarClick}
                disabled={isAvatarUploading}
                className="bg-primary text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors duration-200"
              >
                {isAvatarUploading ? texts.uploading : texts.uploadPhoto}
              </button>
              <button
                type="button"
                onClick={handleRemoveAvatar}
                disabled={isAvatarUploading}
                className="border border-border text-text-secondary px-4 py-2 rounded-md text-sm font-medium hover:bg-secondary-100 disabled:opacity-60 transition-colors duration-200"
              >
                {texts.removePhoto}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label htmlFor="fullName" className="block text-sm font-medium text-text-primary mb-2">
              {texts.fullName} <span className="text-error">*</span>
            </label>
            <input
              type="text"
              id="fullName"
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              className={`block w-full px-4 py-3 border rounded-md shadow-sm focus:border-border-focus focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 bg-background text-text-primary placeholder-text-secondary ${
                fieldErrors.fullName ? 'border-error focus:ring-error/20' : 'border-border'
              }`}
              placeholder={texts.fullNamePlaceholder}
              required
            />
            {fieldErrors.fullName && (
              <p className="mt-2 text-sm text-error">{fieldErrors.fullName}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
              {texts.email} <span className="text-error">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`block w-full px-4 py-3 border rounded-md shadow-sm focus:border-border-focus focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 bg-background text-text-primary placeholder-text-secondary ${
                fieldErrors.email ? 'border-error focus:ring-error/20' : 'border-border'
              }`}
              placeholder={texts.emailPlaceholder}
              required
            />
            {fieldErrors.email && (
              <p className="mt-2 text-sm text-error">{fieldErrors.email}</p>
            )}
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-text-primary mb-2">
              {texts.phone}
            </label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className="block w-full px-4 py-3 border border-border rounded-md shadow-sm focus:border-border-focus focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 bg-background text-text-primary placeholder-text-secondary"
              placeholder={texts.phonePlaceholder}
            />
          </div>

          <div>
            <label htmlFor="location" className="block text-sm font-medium text-text-primary mb-2">
              {texts.location}
            </label>
            <input
              type="text"
              id="location"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              className="block w-full px-4 py-3 border border-border rounded-md shadow-sm focus:border-border-focus focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 bg-background text-text-primary placeholder-text-secondary"
              placeholder={texts.locationPlaceholder}
            />
          </div>

          <div>
            <label htmlFor="website" className="block text-sm font-medium text-text-primary mb-2">
              {texts.website}
            </label>
            <input
              type="url"
              id="website"
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              className="block w-full px-4 py-3 border border-border rounded-md shadow-sm focus:border-border-focus focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 bg-background text-text-primary placeholder-text-secondary"
              placeholder={texts.websitePlaceholder}
            />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="bio" className="block text-sm font-medium text-text-primary mb-2">
              {texts.bio}
            </label>
            <textarea
              id="bio"
              rows={4}
              value={formData.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              className="block w-full px-4 py-3 border border-border rounded-md shadow-sm focus:border-border-focus focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 bg-background text-text-primary placeholder-text-secondary resize-none"
              placeholder={texts.bioPlaceholder}
              maxLength={500}
            />
            <p className="text-xs text-text-secondary mt-1">
              {formData.bio.length}/500 {texts.characterCount}
            </p>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end">
          <button
            type="submit"
            disabled={isSaving || isAvatarUploading}
            className="bg-primary text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-primary-700 disabled:opacity-60 transition-colors duration-200"
          >
            {isSaving ? texts.saving : texts.saveChanges}
          </button>
        </div>
      </form>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

export default ProfileInformation;
